import { Card } from "@/src/components/wrappers/Card";
import { useEffect, useRef, useState } from "react";
import { Yacht } from "../../Yachts/model";
import { usePageSize, useSorting } from "./state";
import qs from "qs";
import { boatsFetch } from "@/src/lib/boatsFetch";
import { YachtsListing } from "./YachtsListing";
import { ShortlistRail } from "./ShortlistRail";
import { SearchBar } from "./SearchBar";
import { List, CalendarRange } from "lucide-react";
import { useOffersStore } from "@/src/stores/offersStore";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/src/components/ui/dialog";
import { Button } from "@/src/components/ui/button";
import { Plus } from "lucide-react";
import { Input } from "@/src/components/ui/input";
import { Label } from "@/src/components/ui/label";
import { useFetch } from "@/src/lib/useFetch";
import { emptyYachtMeta, serializeYachtForm, YachtForm } from "../../Yachts/EditYacht/yacht.schema";
import { useToast } from "@/src/components/ui/use-toast";
import { Operator } from "../../Operators/model";
import { Model } from "../../Yachts/EditYacht/model";
import { Location } from "../../Locations/model";
import { Loader } from "./ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { useOldSearch, YachtsData } from "./useOldSearch";
import { YachtsFiltersFormT } from "./YachtsFiltersForm/yachts-filters.schema";
import { logOfferCreatorAction } from "../offerCreatorLogs";

type YachtfolioYachtData = {
  builder: string;
  name: string;
  year: number;
  photos: { id: string; name: string; url: string }[];
  beam: number;
  berths: number;
  length: number;
  heads: number;
  crewNumber: number;
  maxPeople: number;
  extraSpecs: { name: string; value: string }[];
  id?: string;
  operator: Pick<Operator, "name" | "id">;
  model: Pick<Model, "name" | "id">;
  location: Pick<Location, "names" | "id">;
  description: string;
  cabins?: number;
};

async function fetchSearch(query: string) {}

export function Yachts({
  openChangeDetails,
  onQuickAdd,
  onAddWithDates,
}: {
  openChangeDetails: (id: string, index?: number) => void;
  onQuickAdd?: (id: string, fallback?: { name?: string; media?: string[]; slug?: string }) => void;
  onAddWithDates?: (id: string, from: string, to: string) => void;
}) {
  const [yachts, setYachts] = useState<YachtsData>({
    data: [],
    totalCount: 1,
    page: 1,
    rowsPerPage: 40,
  });
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [yachtfolioModal, setYachtfolioModal] = useState(false);
  const [yachtfolioInput, setYachtfolioInput] = useState("");
  const [yfData, setYfData] = useState<YachtfolioYachtData | null>(null);
  const [yfExisting, setYfExisting] = useState<{ id: string; name: string; apiSynced?: boolean } | null>(null);
  const [yfFetching, setYfFetching] = useState(false);
  const [yfHighRate, setYfHighRate] = useState("");
  const [yfLowRate, setYfLowRate] = useState("");
  const [yfCurrency, setYfCurrency] = useState("EUR");
  const [yfApa, setYfApa] = useState("30");
  const [yfVat, setYfVat] = useState("13");
  const [yfInclusive, setYfInclusive] = useState(false);
  const [yfPaste, setYfPaste] = useState("");
  const [yfImporting, setYfImporting] = useState(false);
  const { yachtsFilters } = useOffersStore();
  const pageSize = usePageSize();
  const sort = useSorting();
  const { fetch, isFetching } = useFetch();

  const toast = useToast();

  const query = yachtsFilters ? stringifyYachtParams(yachtsFilters) : "";
  const sortQuery = sort === "recommended" ? "" : `&sort=${sort}`;

  const { search: fetchYachts } = useOldSearch();

  useEffect(() => {
    const requestUrlOrParams = `?page=${page}&mode=offers&rowsPerPage=${pageSize}&${query}${sortQuery}`;
    fetchYachts(requestUrlOrParams).then((data) => {
      setYachts(data);
      logOfferCreatorAction({
        eventType: "yacht_search.executed",
        meta: {
          filters: yachtsFilters,
          page,
          page_size: pageSize,
          sort,
          result_count: data?.totalCount,
          returned_yacht_ids: (data?.data ?? []).map((yacht) => yacht.id),
          request_url_or_params: requestUrlOrParams,
        },
      });
    });
  }, [page, pageSize, query, sort]);

  useEffect(() => {
    if (document.querySelector(".scrollarea")) {
      document.querySelector(".scrollarea > div")!.scrollTo({
        behavior: "smooth",
        top: 0,
      });
    }
  }, [page]);

  // Any change to the filters, sort or page size returns to page 1 — otherwise the new
  // (usually smaller) result set is requested at the old page number, so the matches
  // appear to be "hidden" behind pages that no longer hold them.
  const isFirstQuery = useRef(true);
  useEffect(() => {
    if (isFirstQuery.current) {
      isFirstQuery.current = false;
      return;
    }
    setPage(1);
  }, [query, sort, pageSize]);

  // Handoff from the Yachtfolio → Helm browser extension. The extension scrapes the
  // brochure in the broker's own session (past Cloudflare Turnstile), POSTs it to
  // /api/yachts/yachtfolio-import, then drops the created yacht into localStorage and
  // fires this event. We pick it up and open the rate step for that yacht — no link
  // fetch, no navigation away from an in-progress offer.
  useEffect(() => {
    const consumePending = () => {
      let raw: string | null = null;
      try {
        raw = window.localStorage.getItem("helm_yf_pending");
      } catch {
        return;
      }
      if (!raw) return;
      window.localStorage.removeItem("helm_yf_pending");
      let p: any;
      try {
        p = JSON.parse(raw);
      } catch {
        return;
      }
      if (!p?.id) return;
      setYfData(null);
      setYfExisting({ id: p.id, name: p.name ?? "Yacht", apiSynced: !!p.apiSynced });
      const s = p.savedRates;
      if (s) {
        setYfHighRate(s.highWeekly != null ? String(s.highWeekly) : "");
        setYfLowRate(s.lowWeekly != null ? String(s.lowWeekly) : "");
        setYfCurrency(s.currency || "EUR");
        setYfVat(s.vat != null ? String(s.vat) : "");
        setYfApa(s.apa != null ? String(s.apa) : "");
        setYfInclusive(!!s.inclusive);
      } else {
        setYfHighRate("");
        setYfLowRate("");
      }
      setYachtfolioModal(true);
    };
    consumePending();
    window.addEventListener("helm:yf-pending", consumePending);
    window.addEventListener("focus", consumePending);
    return () => {
      window.removeEventListener("helm:yf-pending", consumePending);
      window.removeEventListener("focus", consumePending);
    };
  }, []);

  const resetYf = () => {
    setYachtfolioInput("");
    setYfPaste("");
    setYfData(null);
    setYfExisting(null);
    setYfHighRate("");
    setYfLowRate("");
    setYfCurrency("EUR");
    setYfApa("30");
    setYfVat("13");
    setYfInclusive(false);
  };

  // Collapse the modal's rate inputs into the meta.yfInfo blob the search prices
  // from (yfPeriodPrice does the seasonal day-split server-side). Two bands when
  // both rates given (Jul/Aug high, else low); one band = a flat rate all year.
  const buildYfInfo = () => {
    const high = parseFloat(yfHighRate) || 0;
    const low = parseFloat(yfLowRate) || 0;
    const currency = yfCurrency || "EUR";
    const seasonRates: any[] = [];
    if (high > 0) {
      seasonRates.push({ seasonName: "High (Jul–Aug)", year: "", currency, minRate: high, maxRate: high, terms: "" });
    }
    if (low > 0) {
      seasonRates.push({ seasonName: "Low (rest of year)", year: "", currency, minRate: low, maxRate: low, terms: "" });
    }
    // Store VAT/APA as PERCENTAGES (20, 30) — the convention everywhere else in the
    // system (meta.helmPriceInfo holds 13/20/22 and 30/40, and the offer builder renders
    // "+ VAT ({pct}%)"). Dividing by 100 here wrote fractions, so a broker's 20% VAT
    // reached the offer as 0.2% — i.e. the entered rates looked like they were ignored.
    const vat = yfVat.trim() === "" ? undefined : parseFloat(yfVat) || 0;
    const apa = yfApa.trim() === "" ? undefined : parseFloat(yfApa) || 0;
    return { seasonRates, vat, apa, inclusive: yfInclusive };
  };

  // Step 1: pull the brochure. If the same yacht (name + builder) already exists
  // — possibly added before from another link — recognise it and pre-fill its
  // saved rates so the broker can accept or tweak them.
  const fetchYf = async () => {
    if (!yachtfolioInput.trim() || yfFetching) return;
    logOfferCreatorAction({
      eventType: "yachtfolio_yacht.create_intent",
      meta: { yachtfolio_url: yachtfolioInput },
    });
    setYfFetching(true);
    const { id: toastId } = toast.toast({
      description: <Loader text={"Fetching yacht…"} />,
      variant: "default",
      duration: 50000,
    });
    try {
      const { data } = await fetch<{ data: any }>(`/api/yachts/yachtfolio?url=${encodeURIComponent(yachtfolioInput)}`);
      toast.dismiss(toastId);
      if (data?.id) {
        setYfExisting({ id: data.id, name: data.name, apiSynced: !!data.apiSynced });
        const s = data.savedRates;
        if (s) {
          setYfHighRate(s.highWeekly != null ? String(s.highWeekly) : "");
          setYfLowRate(s.lowWeekly != null ? String(s.lowWeekly) : "");
          setYfCurrency(s.currency || "EUR");
          setYfVat(s.vat != null ? String(s.vat) : "");
          setYfApa(s.apa != null ? String(s.apa) : "");
          setYfInclusive(!!s.inclusive);
        }
      } else {
        setYfData(data as YachtfolioYachtData);
      }
    } catch (error) {
      console.log(error);
      toast.dismiss(toastId);
      toast.toast({
        title: "Couldn’t fetch that yacht",
        description: "The Yachtfolio link looks invalid or is protected.",
        duration: 4000,
        variant: "destructive",
      });
    } finally {
      setYfFetching(false);
    }
  };

  // Apply an import/existing result (from paste, extension handoff, or an existing
  // match) to the rate step: recognise the yacht by id and pre-fill any saved rates.
  const applyYfResult = (data: any) => {
    setYfData(null);
    setYfExisting({ id: data.id, name: data.name ?? "Yacht", apiSynced: !!data.apiSynced });
    const s = data.savedRates;
    if (s) {
      setYfHighRate(s.highWeekly != null ? String(s.highWeekly) : "");
      setYfLowRate(s.lowWeekly != null ? String(s.lowWeekly) : "");
      setYfCurrency(s.currency || "EUR");
      setYfVat(s.vat != null ? String(s.vat) : "");
      setYfApa(s.apa != null ? String(s.apa) : "");
      setYfInclusive(!!s.inclusive);
    } else {
      setYfHighRate("");
      setYfLowRate("");
    }
  };

  // Cleaner path: broker pastes the JSON their scraper (bookmarklet/extension) copied
  // from the rendered brochure. Same-origin import — no cross-site cookies, no CAPTCHA.
  const importPasted = async () => {
    let parsed: any;
    try {
      parsed = JSON.parse(yfPaste.trim());
    } catch {
      toast.toast({ title: "That doesn’t look like yacht data", description: "Paste the JSON your scraper copied.", variant: "destructive", duration: 4000 });
      return;
    }
    setYfImporting(true);
    const { id: toastId } = toast.toast({ description: <Loader text={"Importing yacht…"} />, variant: "default", duration: 50000 });
    try {
      const { data } = await fetch<{ data: any }>(`/api/yachts/yachtfolio-import`, "POST", { data: parsed });
      toast.dismiss(toastId);
      if (!data?.id) throw new Error("No yacht returned");
      applyYfResult(data);
    } catch (e: any) {
      toast.dismiss(toastId);
      toast.toast({ title: "Import failed", description: e?.message ?? "Could not import that yacht.", variant: "destructive", duration: 4000 });
    } finally {
      setYfImporting(false);
    }
  };

  // Step 2: add to the offer. Existing yacht → save any rate edits, then open the
  // configure modal. New yacht → create it (hidden from public) carrying the
  // seasonal rates, then open configure.
  const addYf = async () => {
    const yfInfo = buildYfInfo();

    if (yfExisting) {
      const id = yfExisting.id;
      // Already in the Yachtfolio API — it carries live rates, so don't overwrite
      // them; just drop it into the offer.
      if (yfExisting.apiSynced) {
        setYachtfolioModal(false);
        resetYf();
        openChangeDetails(id);
        return;
      }
      const { id: toastId } = toast.toast({
        description: <Loader text={"Adding yacht…"} />,
        variant: "default",
        duration: 50000,
      });
      try {
        await fetch(`/api/yachts/yacht/${id}/yachtfolio-rates`, "PATCH", { yfInfo });
        toast.dismiss(toastId);
        setYachtfolioModal(false);
        resetYf();
        openChangeDetails(id);
      } catch (e: any) {
        toast.dismiss(toastId);
        toast.toast({ title: "Failed to add yacht", description: e?.message ?? "", variant: "destructive" });
      }
      return;
    }

    if (!yfData) return;
    const { id: toastId } = toast.toast({
      description: <Loader text={"Creating yacht…"} />,
      variant: "default",
      duration: 50000,
    });
    const basePrice = parseFloat(yfHighRate) || parseFloat(yfLowRate) || 1;
    const yachtData: YachtForm = {
      meta: { ...emptyYachtMeta, yfInfo } as any,
      amenities: [],
      basicDetails: {
        anchorVisibility: "hidden",
        helmVisibility: "hidden",
        offerCreatorVisibility: "visible",
        charterTypes: { bareboat: false, crewed: true, skippered: false },
        description: yfData.description ?? "",
        manufacturer: yfData.builder,
        name: yfData.name,
        operator: yfData.operator,
        model: yfData.model,
        provider: "Yachtfolio",
        productionYear: yfData.year,
        type: "Motor yacht",
        photos: yfData.photos,
        labels: [],
        startDay: "0",
        minimumCharterDuration: 1,
        notes: "",
        ratingAnchor: null,
        ratingHelm: null,
      },
      locationAndPricing: {
        pricing: {
          price: basePrice,
          currency: yfCurrency || "EUR",
          type: "simple",
          per: "week",
          vat: yfInfo.vat ?? 0,
          apa: yfInfo.apa ?? 0,
          location: yfData.location,
        },
        securityDeposit: { currency: "EUR", price: 0 },
      },
      specification: {
        beam: yfData.beam,
        berths: yfData.berths,
        cabins: yfData.cabins ?? 0,
        crewCabins: 0,
        heads: 0,
        crewNumber: yfData.crewNumber,
        extraSpecs: yfData.extraSpecs,
        length: yfData.length,
        mainsailType: "not_available",
        maxPeople: yfData.maxPeople,
      },
    };

    await saveYacht(yachtData, toastId).then((res: any) => {
      const id = res?.data?.id;
      setYachtfolioModal(false);
      resetYf();
      if (id) openChangeDetails(id);
    });
  };

  const saveYacht = async (data: YachtForm, toastId: string) => {
    if (!isFetching) {
      try {
        const payload = serializeYachtForm(data);
        const res = await fetch("/api/yachts/yachts", "POST", payload);
        toast.dismiss(toastId);
        toast.toast({
          title: "Yacht change",
          description: "The yacht has been saved",
          variant: "default",
        });
        setYachtfolioInput("");
        return res;
      } catch (e: any) {
        console.error(e);
        toast.dismiss(toastId);
        toast.toast({
          title: "Failed to save yacht",
          description: e.message || "An error occurred while saving the yacht",
          variant: "destructive",
        });
        setYachtfolioInput("");
      }
    }
  };

  const yfShowRates = !!(yfData || yfExisting);

  return (
    <Card>
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold col-span-full mb-4">Yachts</h3>
          <Dialog
            onOpenChange={(v) => {
              setYachtfolioModal(v);
              if (!v) resetYf();
            }}
            open={yachtfolioModal}
          >
            <DialogTrigger asChild>
              <Button className="bg-white" variant="outline">
                Yachtfolio
                <Plus className="ml-2" size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px] max-h-[calc(100%-80px)] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-3xl">Add Yachtfolio yacht</DialogTitle>
              </DialogHeader>

              {!yfShowRates ? (
                <div className="py-4 flex flex-col gap-2">
                  <Label>Paste yacht data</Label>
                  <textarea
                    className="min-h-[120px] rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
                    placeholder='Paste the yacht data your scraper copied (starts with { "name": … )'
                    value={yfPaste}
                    onChange={(e) => setYfPaste(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    Open the brochure in your browser, run the <b>Yachtfolio → Helm</b> scraper (bookmarklet or
                    extension) to copy the yacht, then paste it here. It won’t be published on the website — it’s only
                    added to this offer.
                  </p>

                  <details className="mt-1">
                    <summary className="text-xs text-slate-500 cursor-pointer">Or paste a brochure link (legacy)</summary>
                    <div className="flex flex-col gap-2 mt-2">
                      <Input
                        placeholder="Paste a public brochure link…"
                        value={yachtfolioInput}
                        onChange={(e) => setYachtfolioInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchYf();
                        }}
                      />
                      <p className="text-xs text-slate-400">
                        Only works for older brochure pages — newer Yachtfolio links are bot-protected and must be
                        scraped in your browser.
                      </p>
                      <Button variant="outline" size="sm" onClick={fetchYf} disabled={!yachtfolioInput.trim() || yfFetching}>
                        {yfFetching ? "Fetching…" : "Fetch from link"}
                      </Button>
                    </div>
                  </details>
                </div>
              ) : (
                <div className="py-2 flex flex-col gap-4">
                  <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="font-medium">{yfExisting?.name ?? yfData?.name}</span>
                      <span className="text-xs text-slate-500">
                        {yfExisting?.apiSynced
                          ? "In the Yachtfolio API — live rates will be used"
                          : yfExisting
                            ? "Already in database — pre-filled saved rates"
                            : "New yacht — enter charter rates"}
                      </span>
                    </div>
                    <button type="button" className="text-xs text-blue-600 underline" onClick={resetYf}>
                      Change link
                    </button>
                  </div>

                  {yfExisting?.apiSynced && (
                    <p className="text-sm text-slate-600">
                      This yacht is already synced from the Yachtfolio API, so its live seasonal rates and availability
                      will be used — no need to enter rates. Click <b>Add to offer</b> to configure it.
                    </p>
                  )}

                  {!yfExisting?.apiSynced && (
                  <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">High season (Jul–Aug) — weekly</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 120000"
                        value={yfHighRate}
                        onChange={(e) => setYfHighRate(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">Low season (rest of year) — weekly</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 95000"
                        value={yfLowRate}
                        onChange={(e) => setYfLowRate(e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 -mt-2">
                    Fill both to price a charter that spans seasons (split by day). Fill one if it’s a single flat rate.
                  </p>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">Currency</Label>
                      <select
                        className="h-10 rounded-md border border-slate-200 px-2 text-sm"
                        value={yfCurrency}
                        onChange={(e) => setYfCurrency(e.target.value)}
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">VAT %</Label>
                      <Input
                        type="number"
                        placeholder="13"
                        value={yfVat}
                        onChange={(e) => setYfVat(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-xs">APA %</Label>
                      <Input
                        type="number"
                        placeholder="30"
                        value={yfApa}
                        onChange={(e) => setYfApa(e.target.value)}
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={yfInclusive}
                      onChange={(e) => setYfInclusive(e.target.checked)}
                    />
                    All-inclusive charter (rates include APA / expenses)
                  </label>
                  </>
                  )}
                </div>
              )}

              <DialogFooter>
                {!yfShowRates ? (
                  <Button onClick={importPasted} disabled={!yfPaste.trim() || yfImporting}>
                    {yfImporting ? "Importing…" : "Import yacht"}
                  </Button>
                ) : (
                  <Button onClick={addYf} disabled={!yfExisting?.apiSynced && !yfHighRate && !yfLowRate}>
                    Add to offer
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex items-center justify-between mb-6">
          <h5 className="text-sm">Filter & add yachts to an offer.</h5>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${viewMode === "list" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <List size={15} /> List
            </button>
            <button
              type="button"
              onClick={() => setViewMode("calendar")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border-l border-slate-200 ${viewMode === "calendar" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              <CalendarRange size={15} /> Calendar
            </button>
          </div>
        </div>
        <SearchBar />
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className="min-w-0">
            <YachtsListing
              viewMode={viewMode}
              onAddWithDates={onAddWithDates}
              openChangeDetails={openChangeDetails}
              onQuickAdd={onQuickAdd}
              items={yachts.data}
              pagination={{
                pagination: {
                  page: yachts.page,
                  pageSize: yachts.rowsPerPage,
                },
                total: yachts.totalCount,
              }}
              onPageClick={(page) => {
                setPage(page);
              }}
            />
          </div>
          <ShortlistRail sticky />
        </div>
      </div>
    </Card>
  );
}

function stringifyYachtParams(p: YachtsFiltersFormT): string {
  p = { ...p };
  // Default to synced + delayed when the broker hasn't explicitly picked a
  // status. The search only carries dirty filter fields, so apiStatus is usually
  // `undefined` (not `[]`) — the old `!(length !== 0)` check missed that case and
  // silently requested synced-only, hiding the whole delayed fleet (e.g. staging's
  // capped-sync BM/NS bareboat yachts → "No results" for a bareboat search).
  // "expired" is deliberately NOT in the default: those are dead records the
  // provider no longer returns ("Not currently in API" badge) — they can't be
  // priced or booked and their public yacht pages 404, so surfacing them by
  // default buries the live fleet (amenity filters made whole pages of them).
  // Brokers can still opt in via the Filters modal's API status checkboxes.
  if (!p.apiStatus?.length) {
    p.apiStatus = ["delayed", "synced"];
  }
  const query = qs.stringify(p, { indices: false });
  return query;
}
